<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import invitationsHttpService, {
  type BatchListItem,
  type ImportPreview,
  type ResultsBatchListItem,
} from '@/services/http/invitationsHttpService.ts'
import EventSelect from '@/components/events/EventSelect.vue'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, locale } = useI18n()
const toast = useToast()
const confirm = useConfirm()
const router = useRouter()
const queryClient = useQueryClient()

// Активная вкладка хранится в URL (?tab=…) — переживает обновление страницы. Порядок ключей =
// порядку TabPanel (TabView в PrimeVue 4 выбирает по индексу). Начальное значение читаем из route
// при монтировании, а на смену пишем адрес через history.replaceState — БЕЗ навигации роутера:
// иначе на каждый клик вкладки прогоняется beforeEach-гвард (декод JWT, проверка ролей) и лишняя
// перерисовка, из-за чего переключение подвисает.
const route = useRoute()
const TAB_KEYS = ['invitations', 'results'] as const
function tabIndexFromQuery(): number {
  const i = TAB_KEYS.indexOf(String(route.query.tab) as (typeof TAB_KEYS)[number])
  return i >= 0 ? i : 0
}
const activeTabIndex = ref(tabIndexFromQuery())
// Ленивый рендер вкладок: TabView держит все панели в DOM, поэтому контент рендерим только
// у активной.
const activeTabKey = computed(() => TAB_KEYS[activeTabIndex.value] ?? TAB_KEYS[0])
watch(activeTabIndex, (i) => {
  const key = TAB_KEYS[i] ?? TAB_KEYS[0]
  const url = new URL(window.location.href)
  if (url.searchParams.get('tab') === key) return
  url.searchParams.set('tab', key)
  window.history.replaceState(window.history.state, '', url)
})

// --- Типы сообщений + превью шаблона ---

const typesQuery = useQuery({
  queryKey: computed(() => ['admin', 'invitations', 'types', locale.value]),
  queryFn: () => invitationsHttpService.getTypes(),
})

const messageTypeCode = ref<string | null>(null)

// Дефолтный выбор — первый тип (сейчас единственный Invitation).
watch(
  () => typesQuery.data.value,
  (types) => {
    if (messageTypeCode.value == null && types && types.length > 0) {
      messageTypeCode.value = types[0].code
    }
  },
  { immediate: true },
)

const typeOptions = computed(() =>
  (typesQuery.data.value ?? []).map((type) => ({
    label: type.name ?? type.code,
    value: type.code,
  })),
)

const selectedType = computed(() =>
  (typesQuery.data.value ?? []).find((type) => type.code === messageTypeCode.value),
)

// --- Исключения (по умолчанию включены) ---

const excludeRegistered = ref(true)
const excludeFromPreviousBatches = ref(true)

// --- Скачивание шаблона ---

const downloadingTemplate = ref(false)

async function onDownloadTemplate() {
  downloadingTemplate.value = true
  try {
    await invitationsHttpService.downloadTemplate()
  } catch {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('invitations.toasts.templateFailed'),
      life: 3000,
    })
  } finally {
    downloadingTemplate.value = false
  }
}

// --- Импорт файла ---

const preview = ref<ImportPreview | null>(null)

const importMutation = useMutation({
  mutationFn: (file: File) =>
    invitationsHttpService.importFile(file, {
      messageTypeCode: messageTypeCode.value ?? '',
      excludeRegistered: excludeRegistered.value,
      excludeFromPreviousBatches: excludeFromPreviousBatches.value,
    }),
  onSuccess: (result) => {
    preview.value = result
    queryClient.invalidateQueries({ queryKey: ['admin', 'invitations', 'batches'] })
  },
  onError: (error: unknown) => {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: error instanceof Error ? error.message : t('invitations.toasts.importFailed'),
      life: 5000,
    })
  },
})

// FileUpload в режиме custom: сами обрабатываем выбор файла, без авто-аплоада.
function onFileSelect(event: { files: File[] }) {
  const file = event.files?.[0]
  if (!file || messageTypeCode.value == null) return
  importMutation.mutate(file)
}

function openBatch(batchId: string) {
  router.push(`/invitations/${batchId}`)
}

// --- Список партий приглашений ---

const batchesQuery = useQuery({
  queryKey: computed(() => ['admin', 'invitations', 'batches', locale.value]),
  queryFn: () => invitationsHttpService.listBatches(),
})

const batches = computed<BatchListItem[]>(() => batchesQuery.data.value ?? [])

// Общая воронка по всем рассылкам — агрегат списка партий (боксами, как в детали/Документах).
// Цвет числа по логике этапа: отправлено — голубой (в пути), доставлено — бирюзовый (успех),
// клики — фиолетовый (вовлечённость), регистрации — зелёный (цель). «Всего» — нейтральный.
const aggregateFunnelBoxes = computed(() => {
  const bs = batches.value
  const sum = (pick: (b: BatchListItem) => number) => bs.reduce((s, b) => s + pick(b), 0)
  return [
    {
      key: 'total',
      label: t('invitations.detail.funnel.total'),
      count: sum((b) => b.validCount),
      color: '',
    },
    {
      key: 'sent',
      label: t('invitations.detail.funnel.sent'),
      count: sum((b) => b.sentCount),
      color: 'text-sky-600 dark:text-sky-400',
    },
    {
      key: 'delivered',
      label: t('invitations.detail.funnel.delivered'),
      count: sum((b) => b.deliveredCount),
      color: 'text-teal-600 dark:text-teal-400',
    },
    {
      key: 'clicked',
      label: t('invitations.detail.funnel.clicked'),
      count: sum((b) => b.clickedCount),
      color: 'text-violet-600 dark:text-violet-400',
    },
    {
      key: 'registered',
      label: t('invitations.detail.funnel.registered'),
      count: sum((b) => b.registeredCount),
      color: 'text-green-600 dark:text-green-400',
    },
  ]
})

function formatDateTime(value: string): string {
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// --- Вкладка «Результаты»: реестр партий рассылки результатов ---

const resultsQuery = useQuery({
  queryKey: computed(() => ['admin', 'results-broadcast', 'batches', locale.value]),
  queryFn: () => invitationsHttpService.getResultsBatches(),
})

const resultsBatches = computed<ResultsBatchListItem[]>(() => resultsQuery.data.value ?? [])

function openResultsBatch(id: string) {
  router.push(`/invitations/results/${id}`)
}

// Событие для рассылки результатов — единый EventSelect (дефолт по featured-событию).
const resultsEventId = ref<string | null>(null)

const broadcastMutation = useMutation({
  mutationFn: (eventId: string) => invitationsHttpService.triggerResultsBroadcast(eventId),
  onSuccess: (result) => {
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('invitations.results.toasts.success', { n: result.invitationCount }),
      life: 4000,
    })
    resultsQuery.refetch()
  },
  onError: (error: unknown) => {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: error instanceof Error ? error.message : t('invitations.results.toasts.failed'),
      life: 5000,
    })
  },
})

// Рассылка уходит всем не отменённым участникам события — обязательное подтверждение.
function confirmBroadcast() {
  if (!resultsEventId.value) return
  confirm.require({
    message: t('invitations.results.confirm.message'),
    header: t('invitations.results.confirm.header'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('invitations.results.broadcastButton'), severity: 'warn' },
    accept: () => broadcastMutation.mutate(resultsEventId.value!),
  })
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <h1 class="text-xl font-bold">{{ t('invitations.title') }}</h1>

    <TabView v-model:activeIndex="activeTabIndex" class="w-full invitations-tabs">
      <!-- ───────── Вкладка: Приглашения ───────── -->
      <TabPanel value="invitations" :header="t('invitations.tabs.invitations')">
        <div
          v-if="activeTabKey === 'invitations'"
          class="flex flex-col gap-4 pt-2 invitations-tab-body"
        >
          <!-- Карточка формирования рассылки -->
          <Card>
            <template #content>
              <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-1 sm:max-w-md">
                  <label for="messageType">{{ t('invitations.form.messageType') }}</label>
                  <Select
                    id="messageType"
                    v-model="messageTypeCode"
                    class="w-full"
                    :options="typeOptions"
                    option-label="label"
                    option-value="value"
                    :loading="typesQuery.isLoading.value"
                    :placeholder="t('invitations.form.messageTypePlaceholder')"
                  />
                </div>

                <!-- Превью шаблона SMS по локалям -->
                <div v-if="selectedType" class="flex flex-col gap-2">
                  <span class="text-sm font-semibold">{{
                    t('invitations.form.templatePreview')
                  }}</span>
                  <div class="flex flex-col gap-2">
                    <div
                      v-for="loc in ['ru', 'kz', 'en'] as const"
                      :key="loc"
                      class="flex flex-col gap-1 rounded border border-surface-200 dark:border-surface-700 p-3"
                    >
                      <span class="text-xs uppercase text-surface-500">{{
                        t(`invitations.locales.${loc}`)
                      }}</span>
                      <span class="text-sm whitespace-pre-wrap">{{
                        selectedType.templates[loc]
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2">
                    <Checkbox input-id="excludeRegistered" v-model="excludeRegistered" binary />
                    <label for="excludeRegistered" class="text-sm">{{
                      t('invitations.form.excludeRegistered')
                    }}</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <Checkbox
                      input-id="excludeFromPreviousBatches"
                      v-model="excludeFromPreviousBatches"
                      binary
                    />
                    <label for="excludeFromPreviousBatches" class="text-sm">{{
                      t('invitations.form.excludeFromPreviousBatches')
                    }}</label>
                  </div>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                  <Button
                    :label="t('invitations.form.downloadTemplate')"
                    icon="pi pi-download"
                    severity="secondary"
                    outlined
                    :loading="downloadingTemplate"
                    @click="onDownloadTemplate"
                  />
                  <FileUpload
                    mode="basic"
                    custom-upload
                    auto
                    accept=".csv,.xlsx"
                    :choose-label="t('invitations.form.chooseFile')"
                    :disabled="messageTypeCode == null || importMutation.isPending.value"
                    @select="onFileSelect"
                  />
                  <i v-if="importMutation.isPending.value" class="pi pi-spin pi-spinner" />
                </div>
              </div>
            </template>
          </Card>

          <!-- Превью результата импорта -->
          <Card v-if="preview">
            <template #title>{{ t('invitations.preview.title') }}</template>
            <template #content>
              <div class="flex flex-col gap-4">
                <div class="flex flex-wrap gap-2">
                  <Tag
                    severity="success"
                    :value="`${t('invitations.preview.valid')}: ${preview.valid}`"
                  />
                  <Tag
                    severity="secondary"
                    :value="`${t('invitations.preview.duplicatesInFile')}: ${preview.duplicatesInFile}`"
                  />
                  <Tag
                    :severity="preview.errors > 0 ? 'danger' : 'secondary'"
                    :value="`${t('invitations.preview.errors')}: ${preview.errors}`"
                  />
                  <Tag
                    severity="warn"
                    :value="`${t('invitations.preview.excludedRegistered')}: ${preview.excludedRegistered}`"
                  />
                  <Tag
                    severity="warn"
                    :value="`${t('invitations.preview.excludedPreviousBatches')}: ${preview.excludedPreviousBatches}`"
                  />
                </div>

                <DataTable :value="preview.errorRows" data-key="rowNumber" class="w-full">
                  <template #empty>
                    <div class="text-sm text-surface-500 py-4">
                      {{ t('invitations.preview.errorsEmpty') }}
                    </div>
                  </template>
                  <Column field="rowNumber" :header="t('invitations.preview.rowNumber')" />
                  <Column field="error" :header="t('invitations.preview.error')" />
                </DataTable>

                <div>
                  <Button
                    :label="t('invitations.preview.openBatch')"
                    icon="pi pi-arrow-right"
                    icon-pos="right"
                    @click="openBatch(preview.batchId)"
                  />
                </div>
              </div>
            </template>
          </Card>

          <!-- Общая воронка по всем рассылкам (агрегат списка). -->
          <div v-if="batches.length" class="flex flex-col gap-2">
            <span class="font-semibold text-sm">{{ t('invitations.funnel.title') }}</span>
            <div
              class="grid grid-cols-2 sm:grid-cols-5 gap-2 w-full"
              data-test="invitations-funnel"
            >
              <div
                v-for="box in aggregateFunnelBoxes"
                :key="box.key"
                class="flex flex-col gap-1 rounded border border-surface-200 dark:border-surface-700 p-3"
                :data-test="`funnel-${box.key}`"
              >
                <span class="text-xs text-surface-500">{{ box.label }}</span>
                <span class="text-2xl font-bold" :class="box.color">{{ box.count }}</span>
              </div>
            </div>
          </div>

          <!-- Список партий рассылок -->
          <DataTable
            :value="batches"
            :loading="batchesQuery.isLoading.value"
            data-key="id"
            class="w-full"
            selection-mode="single"
            @row-click="openBatch(($event.data as BatchListItem).id)"
          >
            <template #empty>
              <div class="text-sm text-surface-500 py-4">{{ t('invitations.batches.empty') }}</div>
            </template>

            <Column field="fileName" :header="t('invitations.batches.columns.fileName')" />
            <Column :header="t('invitations.batches.columns.eventName')">
              <template #body="{ data }">{{ data.eventName ?? '—' }}</template>
            </Column>
            <Column :header="t('invitations.batches.columns.createdAt')">
              <template #body="{ data }">{{ formatDateTime(data.createdAt) }}</template>
            </Column>
            <Column :header="t('invitations.batches.columns.sentAt')">
              <template #body="{ data }">{{
                data.sentAt ? formatDateTime(data.sentAt) : '—'
              }}</template>
            </Column>
            <Column field="totalRows" :header="t('invitations.batches.columns.total')" />
            <Column field="validCount" :header="t('invitations.batches.columns.valid')" />
            <Column field="sentCount" :header="t('invitations.batches.columns.sent')" />
            <Column field="deliveredCount" :header="t('invitations.batches.columns.delivered')" />
            <Column field="clickedCount" :header="t('invitations.batches.columns.clicked')" />
            <Column field="registeredCount" :header="t('invitations.batches.columns.registered')" />
          </DataTable>
        </div>
      </TabPanel>

      <!-- ───────── Вкладка: Результаты ───────── -->
      <TabPanel value="results" :header="t('invitations.tabs.results')">
        <div
          v-if="activeTabKey === 'results'"
          class="flex flex-col gap-4 pt-2 invitations-tab-body"
        >
          <div class="flex flex-wrap items-center gap-3">
            <EventSelect v-model="resultsEventId" data-test="results-event-select" />
            <Button
              :label="t('invitations.results.broadcastButton')"
              icon="pi pi-send"
              severity="success"
              :disabled="!resultsEventId || broadcastMutation.isPending.value"
              :loading="broadcastMutation.isPending.value"
              data-test="btn-broadcast-results"
              @click="confirmBroadcast"
            />
          </div>

          <DataTable
            :value="resultsBatches"
            :loading="resultsQuery.isLoading.value"
            data-key="id"
            class="w-full"
            selection-mode="single"
            @row-click="openResultsBatch(($event.data as ResultsBatchListItem).id)"
          >
            <template #empty>
              <div class="text-sm text-surface-500 py-4">
                {{ t('invitations.results.empty') }}
              </div>
            </template>

            <Column :header="t('invitations.results.columns.eventName')">
              <template #body="{ data }">{{ data.eventName ?? '—' }}</template>
            </Column>
            <Column :header="t('invitations.results.columns.createdAt')">
              <template #body="{ data }">{{ formatDateTime(data.createdAt) }}</template>
            </Column>
            <Column :header="t('invitations.results.columns.sentAt')">
              <template #body="{ data }">{{
                data.sentAt ? formatDateTime(data.sentAt) : '—'
              }}</template>
            </Column>
            <Column field="recipients" :header="t('invitations.results.columns.recipients')" />
            <Column field="sentCount" :header="t('invitations.results.columns.sent')" />
            <Column field="clickedCount" :header="t('invitations.results.columns.clicked')" />
            <Column field="downloadedCount" :header="t('invitations.results.columns.downloaded')" />
            <Column field="sharedCount" :header="t('invitations.results.columns.shared')" />
          </DataTable>
        </div>
      </TabPanel>
    </TabView>
  </section>
</template>

<style scoped>
.invitations-tabs {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.invitations-tabs :deep(.p-tabview-panels) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
}
/* Активная панель заполняет высоту (скрытые PrimeVue гасит inline display:none). */
.invitations-tabs :deep(.p-tabview-panel) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.invitations-tab-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
